import { ActionTree } from 'vuex'
import { SharedFoldersState } from './sharedFolder.types'
import { MutationTypes } from './sharedFolder.mutations'
import orderBy from 'lodash/orderBy'
import { splitSortOption } from '@/common/splitSortOption'

import { sharedFoldersApi } from 'vue-common/src/services/shared-folders.service'
import { productTechpacksApi, productTechpackSharesApi } from 'vue-common/src/services/techpack.service'
import { EmailProductTechpackDto, SharedFolder, SharedFolderDTO, UpdateSharedFolderDTO } from 'vue-common/src/generated'

export const actions: ActionTree<SharedFoldersState, unknown> = {
  async createSharedFolder({ commit, dispatch }, folder: SharedFolderDTO) {
    try {
      commit(MutationTypes.CREATE_FOLDER_REQUEST)

      const { data: createdSharedFolder } = await sharedFoldersApi.createFolder(folder)
      const { data: folders } = await sharedFoldersApi.querySharedFolders()

      commit(MutationTypes.CREATE_FOLDER_SUCCESS, { folders, folder: createdSharedFolder })
      dispatch('snack/setSnack', { text: `Successfully created ${folder.name}`, variant: 'success' }, { root: true })
    } catch (err) {
      commit(MutationTypes.CREATE_FOLDER_FAILURE, err)
      dispatch('snack/setSnack', { text: 'Error creating Folder', variant: 'error' }, { root: true })
    }
  },

  async deleteSharedFolder({ commit, state, dispatch }, id: string) {
    try {
      await sharedFoldersApi.deleteFolder(id)
      const updatedFolders = state.sharedFolders.filter(f => f._id !== id)

      commit(MutationTypes.REMOVE_SHARED_FOLDER)
      commit(MutationTypes.SET_SORTED_FOLDERS, updatedFolders)
      dispatch('snack/setSnack', { text: 'Folder Deleted', variant: 'success' }, { root: true })
    } catch (err) {
      commit(MutationTypes.SET_SHARED_FOLDER_DELETE_ERROR, err)
      dispatch('snack/setSnack', { text: `Error deleting folder`, variant: 'error' }, { root: true })
    }
  },

  async updateSharedFolder(
    { commit, dispatch, state },
    { id, folder, newContactEmails }: { id: string; folder: UpdateSharedFolderDTO; newContactEmails?: number }
  ) {
    try {
      const { data: updatedFolder } = await sharedFoldersApi.updateFolder(id, folder)
      dispatch('getSharedFolder', updatedFolder._id)
      dispatch('getSharedTechpacks', updatedFolder._id)
      if (newContactEmails !== undefined && newContactEmails > 0) {
        dispatch(
          'snack/setSnack',
          { text: `Added ${newContactEmails} collaborators to ${state.sharedFolder?.name}`, variant: 'success' },
          { root: true }
        )
      } else {
        dispatch(
          'snack/setSnack',
          { text: `Successfully updated ${state.sharedFolder?.name}`, variant: 'success' },
          { root: true }
        )
      }
    } catch (err) {
      commit(MutationTypes.SET_SHARED_FOLDER_UPDATE_ERROR, err)
      dispatch('snack/setSnack', { text: `Error adding collaborators`, variant: 'error' }, { root: true })
    }
  },

  async getSharedFolder({ commit }, id: string) {
    try {
      commit(MutationTypes.SHARED_FOLDER_REQUEST)
      const { data: folder } = await sharedFoldersApi.getFolder(id)
      commit(MutationTypes.SET_SHARED_FOLDER, folder)
    } catch (err) {
      commit(MutationTypes.SET_SHARED_FOLDER_GET_ERROR, err)
    }
  },

  async fetchSharedFolders({ commit }) {
    try {
      commit(MutationTypes.SHARED_FOLDERS_REQUEST)
      const { data: folders } = await sharedFoldersApi.querySharedFolders()
      commit(MutationTypes.SET_SHARED_FOLDERS, folders)
    } catch (err) {
      commit(MutationTypes.SET_SHARED_FOLDERS_GET_ERROR, err)
    }
  },

  setSortedFolders({ commit, state }, sortOption) {
    const { option, direction } = splitSortOption<SharedFolder>(sortOption)
    const folders = orderBy(state.sharedFolders, [option], [direction])

    commit(MutationTypes.SET_SORT_OPTION, sortOption)
    commit(MutationTypes.SET_SORTED_FOLDERS, folders)
  },

  async getSharedTechpacks({ commit }, id: string) {
    try {
      commit(MutationTypes.SHARED_FOLDER_TECHPACKS_REQUEST)
      const { data: techpacks } = await sharedFoldersApi.getFolderTechpacks(id)
      commit(MutationTypes.SET_SHARED_FOLDER_TECHPACKS, techpacks)
    } catch (err) {
      commit(MutationTypes.SET_SHARED_FOLDER_TECHPACKS_ERROR, err)
    }
  },

  async removeTechpack({ commit, dispatch }, params: { id: string; techpackId: string }) {
    const { id, techpackId } = params
    try {
      await sharedFoldersApi.removeTechpack(techpackId, id)
      commit(MutationTypes.REMOVE_TECHPACK, techpackId)
      dispatch('snack/setSnack', { text: 'Tech Pack removed', variant: 'success' }, { root: true })
    } catch (err) {
      commit(MutationTypes.REMOVE_TECHPACK_ERROR, err)
      dispatch('snack/setSnack', { text: `Error deleting Techpack`, variant: 'error' }, { root: true })
    }
  },

  async getTechpackSignedUrl({ commit }, { productId, techpackId }) {
    try {
      const { data } = await productTechpacksApi.getSignedUrlForTechpack(techpackId, productId)
      return data
    } catch (err) {
      commit(MutationTypes.SET_SIGNED_URL_ERROR, err)
    }
  },

  async createTechpackShare({ commit }, { productId, techpackId }) {
    try {
      const { data: share } = await productTechpackSharesApi.createProductTechpackShare(techpackId, productId)
      commit(MutationTypes.SET_TECHPACK_SHARE_LINK, { techpackId, share })
    } catch (err) {
      commit(MutationTypes.SET_TECHPACK_SHARE_LINK_ERROR, err)
    }
  },

  async emailTechpack(
    { commit, dispatch },
    params: {
      productId: string
      techpackId: string
      emailData: {
        emails: Array<string>
        subject: string
        body: string
        userInfo: string
        techpackName: string
        shareUrl: string
      }
    }
  ) {
    try {
      const email: EmailProductTechpackDto = {
        receiver_emails: params.emailData.emails,
        email_subject: params.emailData.subject,
        content: {
          email_body: params.emailData.body ? params.emailData.body : 'Click link to view tech pack.',
          report_urls: [{ name: params.emailData.techpackName, url: params.emailData.shareUrl }],
          files: []
        }
      }
      await productTechpacksApi.emailTechpackShare(params.techpackId, params.productId, email)
      dispatch('snack/setSnack', { text: `Email successfully sent`, variant: 'success' }, { root: true })
    } catch (err) {
      commit(MutationTypes.SEND_PUBLIC_LINK_EMAIL_ERROR, err)
      dispatch('snack/setSnack', { text: `Error sending email`, variant: 'error' }, { root: true })
    }
  },
  toggleFolderMenu({ commit }, { id, remove, menu }) {
    commit(MutationTypes.TOGGLE_FOLDER_MENU, { id, remove, menu })
  },
  toggleTechpackMenu({ commit }, { id, remove, share, menu }) {
    commit(MutationTypes.TOGGLE_TECHPACK_MENU, { id, remove, share, menu })
  }
}
